import type { DashboardId, Query } from '@clinspace/shared';
import { QUERY_AGE_BUCKETS, queryAgeDays } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, bars, lines, list, type Rect } from '../charts.ts';
import { queryDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

function bucketOf(age: number): (typeof QUERY_AGE_BUCKETS)[number] {
  return age <= 7 ? '0-7' : age <= 14 ? '8-14' : age <= 30 ? '15-30' : '30+';
}

/** Query Management: live open/answered/closed, aging histogram by site, trend; this is where VR queries appear. */
export class QueryManagementDashboard extends Dashboard {
  readonly id: DashboardId = 'QueryManagement';

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('age:')) return `${segment.slice(4)} days`;
    if (segment.startsWith('query:')) return segment.slice(-6);
    return segment;
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [age, query] = this.path;
    const queries = this.view.queriesFiltered(this.filters);
    if (age && query) {
      const q =
        queries.find((x) => x.id === query.slice(6)) ??
        this.view.queries.find((x) => x.id === query.slice(6));
      if (q) {
        this.detail = queryDetail(this.view, q);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    const open = queries.filter((q) => q.status === 'Open');
    if (age) {
      const bucket = age.slice(4);
      const items = open
        .filter((q) => bucketOf(queryAgeDays(q)) === bucket)
        .sort((a, b) => a.raisedAt.localeCompare(b.raisedAt));
      const rows = list(
        ctx,
        ['Subject', 'Form / field', 'Category', 'Priority', 'Age', 'Raised by'],
        items.map((q) => ({
          cells: [
            q.subjectId,
            `${q.form}${q.field ? ` · ${q.field}` : ''}`,
            q.category,
            q.priority,
            `${queryAgeDays(q)} d`,
            q.raisedBy.name,
          ],
          flag: q.priority === 'High' ? 'x' : undefined,
          colour: PALETTE.crimson,
        })),
        area,
        {
          title: `Open ${bucket} days · ${items.length} queries`,
          widths: [0.14, 0.3, 0.18, 0.1, 0.08, 0.2],
          hoveredIndex: hoveredRow(panel, 'row:'),
          rowH: 20,
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `query:${items[i]!.id}` },
          tooltip: [items[i]!.text.slice(0, 60), 'Open the query'],
        }),
      );
      return;
    }
    const answered = queries.filter((q) => q.status === 'Answered').length;
    const closed = queries.filter((q) => q.status === 'Closed').length;
    const ages = open.map((q) => queryAgeDays(q)).sort((a, b) => a - b);
    const median = ages.length ? ages[Math.floor(ages.length / 2)]! : null;
    const pages = this.view.forms(this.filters).length;
    this.kpis(
      panel,
      ctx,
      [
        { label: 'Open', value: String(open.length), colour: PALETTE.amber, sub: 'live' },
        { label: 'Answered', value: String(answered), colour: PALETTE.teal },
        { label: 'Closed', value: String(closed), colour: PALETTE.forest },
        { label: 'Median days open', value: median === null ? '—' : String(median) },
        {
          label: 'Per 100 CRF pages',
          value: pages ? String(Math.round((queries.length / pages) * 1000) / 10) : '—',
        },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const sites = this.view.file.sites;
    const left: Rect = { x: area.x, y: area.y + 88, w: area.w * 0.52, h: area.h - 88 };
    const data = QUERY_AGE_BUCKETS.map((b) => ({
      label: `${b} d`,
      values: sites.map(
        (s) => open.filter((q) => q.siteId === s.id && bucketOf(queryAgeDays(q)) === b).length,
      ),
    }));
    const rects = bars(ctx, data, left, {
      stacked: true,
      title: 'Open query aging by site',
      hoveredIndex: hoveredRow(panel, 'age:'),
      showValues: true,
    });
    rects.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `age:${i}`,
        ...r,
        data: { kind: 'drill', segment: `age:${QUERY_AGE_BUCKETS[i]}` },
        tooltip: [
          `${QUERY_AGE_BUCKETS[i]} days open`,
          ...sites
            .map((s, si) => `${s.city}: ${data[i]!.values[si]}`)
            .filter((l) => !l.endsWith(': 0')),
          'Open the list',
        ],
      }),
    );
    const right: Rect = {
      x: area.x + area.w * 0.56,
      y: area.y + 88,
      w: area.w * 0.44,
      h: area.h - 88,
    };
    const weeks = this.weekEndings(12);
    const openAt = (day: string) =>
      queries.filter(
        (q) => q.raisedAt.slice(0, 10) <= day && (!q.closedAt || q.closedAt.slice(0, 10) > day),
      ).length;
    const raisedIn = (i: number) =>
      queries.filter(
        (q) =>
          q.raisedAt.slice(0, 10) <= weeks[i]! &&
          (i === 0 || q.raisedAt.slice(0, 10) > weeks[i - 1]!),
      ).length;
    const cols = lines(
      ctx,
      [
        { values: weeks.map(openAt), colour: PALETTE.amber, fill: true, label: 'Open or answered' },
        {
          values: weeks.map((_, i) => raisedIn(i)),
          colour: PALETTE.teal,
          label: 'Raised that week',
        },
      ],
      weeks.map((w) => w.slice(5)),
      right,
      {
        title: 'Unresolved queries over the last 12 weeks',
        hoveredIndex: hoveredRow(panel, 'week:'),
      },
    );
    cols.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `week:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [`Week ending ${weeks[i]}`, `${openAt(weeks[i]!)} open · ${raisedIn(i)} raised`],
        selectable: false,
      }),
    );
  }

  private weekEndings(n: number): string[] {
    const today = new Date();
    return Array.from({ length: n }, (_, i) =>
      new Date(today.getTime() - (n - 1 - i) * 7 * 86_400_000).toISOString().slice(0, 10),
    );
  }

  /** Called by the room when a query arrives or changes. */
  onQuery(q: Query): void {
    this.view.upsertQuery(q);
    this.invalidate();
  }
}
